import * as React from 'react';
import { PROMO_SECTION_TYPE, CONTEXT_GROUP_TYPES, OWNERSHIP_TYPES, UBI_PLUS_STATUS } from '../constants';
import { LOCATION_REGION } from '../localization/locations';
import { LOCALE } from '../localization/locales';

export interface IOwnership {
    readonly gameOwners: number[];
    readonly seasonOwners: number[];
}

export interface IPromoConfig {
    readonly name: string;
    readonly brandId: number;
    readonly language: LOCALE;
    readonly components: IPromoComponentConfig[];
    readonly ownership: IOwnership;
    /** Custom translations (if available) */
    readonly translations?: {
        readonly [key: string]: string
    };
    /** Default translations (if available) */
    readonly defaultTranslations?: {
        readonly [key: string]: string
    };
}

export type PromoSettingsType = IIntroOrVideoSectionConfig | ICarouselSectionConfig | IStepsSectionConfig |
ISalesSectionConfig;

export type IOwnershipContext = {
    readonly [owner in OWNERSHIP_TYPES]: PromoSettingsType;
};

export type IUbiPlusContext = {
    readonly [ubiPlus in UBI_PLUS_STATUS]: PromoSettingsType;
};

export type IRegionsContext = {
    readonly [region in LOCATION_REGION]: PromoSettingsType;
};

export interface IPromoContextGroups {
    [CONTEXT_GROUP_TYPES.COMMON]: PromoSettingsType;
    [CONTEXT_GROUP_TYPES.UBI_PLUS]?: IUbiPlusContext;
    [CONTEXT_GROUP_TYPES.OWNERSHIP]?: IOwnershipContext;
    [CONTEXT_GROUP_TYPES.REGION]?: IRegionsContext;
}

export interface IPromoContextColors {
    [CONTEXT_GROUP_TYPES.COMMON]?: string;
    [CONTEXT_GROUP_TYPES.OWNERSHIP]?: {
        readonly [owner in OWNERSHIP_TYPES]: string;
    };
    [CONTEXT_GROUP_TYPES.UBI_PLUS]?: {
        readonly [ubiPlus in UBI_PLUS_STATUS]: string;
    };
    [CONTEXT_GROUP_TYPES.REGION]?: {
        readonly [region in LOCATION_REGION]: string;
    };
}

export interface IPromoComponentConfig {
    readonly sectionId: string;
    readonly sectionType: PROMO_SECTION_TYPE;
    readonly sectionName: string;
    readonly settings: IPromoContextGroups;
}

type ICommonSectionFields = {
    readonly title: CommonCTAType,
    readonly description: CommonCTAType,
    readonly header: CommonCTAType,
    readonly images: IPromoSectionImages,
    readonly sectionColor?: string,
    readonly isSectionVisible: boolean
};

export type CommonCTAType = {
    visible: boolean,
    hasCustomizeKey?: boolean
};

export type IIntroOrVideoSectionConfig = ICommonSectionFields & {
    readonly logo: ISectionLogoOptions,
    readonly button: {
        visible?: boolean,
        hasCustomizeKey?: boolean,
        uplayNavigationURL?: string,
        connectNavigationURL?: string
    }
};

export type ISalesSectionConfig = ICommonSectionFields & {
    readonly sales: ISalesOfferConfig[]
};

export type ICarouselSectionConfig = ICommonSectionFields;

export type IStepsSectionConfig = ICommonSectionFields & {
    readonly steps: IStep[],
    readonly button: IIntroOrVideoSectionConfig['button']
};

export interface IStep {
    id: number;
    isHidden?: boolean;
}

export interface IPromoImage {
    imgId: string;
    isHidden?: boolean;
}

export interface IPromoSectionImages {
    [role: string]: IPromoImage;
}

export interface ISectionLogoOptions {
    readonly visible: boolean;
    readonly zoom: number;
    readonly isLogoHidden?: boolean;
}

export interface ISalesOfferConfig {
    readonly offerId: string;
    readonly discountTag?: boolean;
    readonly isHidden?: boolean;
}

export interface ISectionInfo {
    readonly type: PROMO_SECTION_TYPE;
    readonly caption: string;
    readonly description: string;
    readonly preview: string;
}

/** Description of styles configuration used in current version of Promo core */
export interface IStyleConfig {
    readonly colors: IGeneralColorConfig[];
    readonly fonts: IFontStyleConfig[];
    readonly buttons: IButtonStylesConfig;
}

export interface IButtonStylesConfig {
    readonly colors: {
        readonly caption: string,
        readonly states: {
            readonly caption: string,
            readonly value: IStyleConfigValue
        }[]
    }[];
    readonly styles: {
        readonly caption: string,
        readonly value: IStyleConfigValue
    }[];
}

export interface IFontStyleConfig {
    /** Name is human readable, but is also used as an object key */
    readonly name: string;
    readonly category: string;
    readonly family: IStyleConfigValue;
    readonly color?: IStyleConfigValue;
    readonly weight?: IStyleConfigValue;
    readonly size?: IStyleConfigValue;
}

export interface IGeneralColorConfig {
    readonly caption: string;
    readonly value: IStyleConfigValue;
}

export interface IStyleConfigValue {
    readonly variable: string;
    readonly defaultValue: string;
}

export interface IRenderToStringOptions {
    readonly config: IPromoConfig;
    readonly coreLocation: string;

    readonly customStylesFile?: string;
    readonly customVariablesFile?: string;
    readonly customFontsFile?: string;
}

/** NOTE: We actually do not implement this interface, because TypeScript does not support static properties on interface */
export interface IPromoBootstrap {
    readonly mount: (node: HTMLElement, config: object, hydrate?: boolean) => void;
    readonly unmount: () => void;
}

export interface IPromoAPI {
    readonly renderToString: (options: IRenderToStringOptions) => Promise<string>;
    readonly getListOfAvailableSections: () => ISectionInfo[];
    readonly getFlatListOfAllKeys: (config?: IPromoConfig) => string[];
    readonly getCSSVariablesAsObject: () => Promise<IStyleConfig>;
}

export interface IPromoCoreExports {
    readonly Bootstrap: IPromoBootstrap;
    readonly PromoAPI: IPromoAPI;
    readonly Root: React.ComponentClass<{
        readonly config: IPromoConfig,
        readonly context?: string,
        readonly user?: IPromoUser,
        readonly contentScrollElement?: HTMLElement | null,
        readonly activeSectionId?: string | null,
        readonly onActiveSectionChange?: (sectionName: string | null) => void
    }>;
}

export type IPromoContextRegion = { value: LOCATION_REGION, label: string };
export type IPromoContextUbiPlus = { value: UBI_PLUS_STATUS, label: string };

export type ImagesDiff = {
    updateList: IPromoSectionImages,
    removeList: string[],
    notChangedList: string[],
    changedList: IPromoSectionImages[]
};

export interface IPromoUser {
    [CONTEXT_GROUP_TYPES.OWNERSHIP]: OWNERSHIP_TYPES | null | undefined;
    [CONTEXT_GROUP_TYPES.REGION]?: LOCATION_REGION | null;
    [CONTEXT_GROUP_TYPES.UBI_PLUS]: UBI_PLUS_STATUS | null;
    countryCode?: string | null;
}

export type DisableCustomizeInheritanceType = Record<'title' | 'description' | 'button' | 'header', {
    visible: boolean | undefined,
    hasCustomizeKey?: boolean
}>;

export interface IPromoTrackOptions {
    type: string;
    target: string;
    section?: string;
    url?: string;
}
